import React, { useEffect, useRef } from 'react';

/**
 * DotConnect — animated network of dots connected by lines.
 * Used for hero & pre-footer backgrounds. Lightweight canvas animation.
 */
const DotConnect = ({
  density = 60,
  color = '#94a3b8',
  lineColor = 'rgba(148,163,184,0.85)',
  accentColor = '#f97316',
  speed = 0.3,
  maxDistance = 130,
  className = ''
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let dots = [];

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.floor((width * height) / 18000) * (density / 60);
      dots = Array.from({ length: Math.max(15, Math.min(140, Math.floor(target))) }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
        r: Math.random() * 1.5 + 0.6,
        accent: Math.random() < 0.08
      }));
    };
    resize();
    window.addEventListener('resize', resize);

    const tick = () => {
      ctx.clearRect(0, 0, width, height);

      // lines
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDistance) {
            const opacity = 1 - dist / maxDistance;
            ctx.strokeStyle = lineColor.replace(/[\d.]+\)$/, `${opacity.toFixed(3)})`);
            ctx.lineWidth = 1.1;
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.stroke();
          }
        }
      }

      // dots
      for (const d of dots) {
        d.x += d.vx;
        d.y += d.vy;
        if (d.x < 0 || d.x > width) d.vx *= -1;
        if (d.y < 0 || d.y > height) d.vy *= -1;

        // soft glow halo
        ctx.beginPath();
        ctx.fillStyle = d.accent ? `${accentColor}40` : `${color}30`;
        ctx.arc(d.x, d.y, d.r * 3, 0, Math.PI * 2);
        ctx.fill();
        // bright core
        ctx.beginPath();
        ctx.fillStyle = d.accent ? accentColor : color;
        ctx.arc(d.x, d.y, d.r + 0.3, 0, Math.PI * 2);
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [density, color, lineColor, accentColor, speed, maxDistance]);

  return <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} aria-hidden="true" />;
};

export default DotConnect;
